import { createContext, useCallback, useContext } from 'react';
import { useFeedQueryContext } from '@core/feed';
import { logError } from '@core/logging';
import { isInPIPIframe } from '@core/pictureInPicture';
import { PlayerLayout, VideoPlayerOpenReason } from '@core/player/types';
import { delegateVideoToPlayer } from '@embed/components/VideoPlayer/helpers';
import { usePlayerStateAnnouncement } from '@embed/components/VideoPlayer/hooks/usePlayerStateAnnouncement';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerActionsSelector } from '@embed/components/VideoPlayer/store/selectors';
/**
 * Provides callbacks to control VideoPlayer layout.
 * @return Object {`minimize`, `maximize`, `close`}
 */
export const LayoutControlsContext = createContext({
    enterMinimized: () => {
        logError(new Error('`enterMinimized` not implemented or `LayoutControlsContext` not provided'));
    },
    enterFullscreen: () => {
        logError(new Error('`enterFullscreen` not implemented or `LayoutControlsContext` not provided'));
    },
});
/**
 * Provider for LayoutControlsContext
 * @param param0
 * @returns
 */
export const LayoutControlsContextProvider = ({ appContext, children, enterPIP, onMinimize, params, video, videos, }) => {
    const videoPlayerActions = useVideoPlayerStore(videoPlayerActionsSelector);
    const announcePlayerDisplayState = usePlayerStateAnnouncement();
    const [{ feed }] = useFeedQueryContext();
    const handleMinimize = useCallback((href) => {
        // Callback with `src` is used by CTA's and Products in usePIPTarget meaning
        // we request potentially iframed PIP.
        // Callback without `src` is used on as click in Player3 PIP UI elements
        // meaning we want to minimize current page.
        if (params.pip_navigate) {
            enterPIP(href);
        }
        onMinimize?.();
        videoPlayerActions.setLayoutByUser(PlayerLayout.MINIMIZED);
        videoPlayerActions.setOpenReasonByUser(VideoPlayerOpenReason.INTERACTION);
        announcePlayerDisplayState(PlayerLayout.MINIMIZED);
    }, [
        announcePlayerDisplayState,
        enterPIP,
        onMinimize,
        params.pip_navigate,
        videoPlayerActions,
    ]);
    const handleMaximize = useCallback(() => {
        if (isInPIPIframe()) {
            delegateVideoToPlayer({
                video,
                appContext,
                params,
                videos,
                feed,
            });
        }
        else {
            videoPlayerActions.setLayoutByUser(PlayerLayout.FULLSCREEN);
            videoPlayerActions.setOpenReasonByUser(VideoPlayerOpenReason.INTERACTION);
            announcePlayerDisplayState(PlayerLayout.FULLSCREEN);
        }
    }, [
        announcePlayerDisplayState,
        appContext,
        feed,
        params,
        video,
        videoPlayerActions,
        videos,
    ]);
    return (<LayoutControlsContext.Provider value={{
            enterMinimized: handleMinimize,
            enterFullscreen: handleMaximize,
        }}>
      {children}
    </LayoutControlsContext.Provider>);
};
/**
 * Use layout controls like `enterMinimized` or `enterFullscreen`
 * via context.
 * @returns
 */
export const useLayoutControls = () => useContext(LayoutControlsContext);
